/**
 ShapeDiver 3D Viewer Application

 CommPlugin.js

 Content:
   Plugin handling the communication with the ShapeDiver model view interface
     - initiating sessions
     - registering parameters with the viewer
     - requesting customizations
     - loading geometry

  Copyright (c) 2018 ShapeDiver GmbH

  @module CommPlugin
 */

var axios = require('../../externals/axios');

/**
 Import constants
 */
var commPluginConstants = require('./CommPluginConstants');

/**
 Import constants
 */
var pluginConstantsGlobal = require('../../shared/constants/PluginConstantsGlobal');

/**
 Import global prototype
 */
var pluginPrototype = require('../PluginPrototype');

/**
 * Messaging constants
 */
var messagingConstants = require('../../shared/constants/MessagingConstants');

/**
 * Messaging constants
 */
var loggingConstants = require('../../shared/constants/LoggingConstants');

/**
 * Global utilities
 */
var GlobalUtils = require('../../shared/util/GlobalUtils');

/**
 * Message prototype
 */
var MessagePrototype = require('../../shared/messages/MessagePrototype');

/**
 * Create a global id for all CommPlugin instances created, to be used as information for communication with the backend
 */
const VIEWER_INSTANCE_ID = GlobalUtils.createRandomId();

/**
 * Build data
 */
const BUILD_DATA = require('../../app/BuildDataAccessor').BuildData;

/**
 * Constructor of the ShapeDiver Communication Plugin
 * @class CommPlugin CommPlugin
 *
 * @implements PluginPrototype
 *
 * @param {Object} settings - Initial settings to be used
 * @param {String} settings.ticket - The ticket defining the model and other attributes of the session
 * @param {String} [settings.authorization] - authorization token to include with requests to the model view interface (prepend 'Bearer ' yourself if necessary)
 * @param {Boolean} [settings.deferGeometryLoading] - Initialise the session but do not load geometry for default parameter values (wait for parameter update)
 * @param {Boolean} [settings.ignoreSuperseded=true] - If truthy ignore intermediate solutions which at the time of their arrival have already been superseded by another customization request.
 *                                                      Caution: In case of continuously repeated customization requests this may lead to the scene never being updated.
 * @param {String} [settings.modelViewUrl='https://model-view.shapediver.com:443'] - The model view interface url
 * @param {String} [settings.runtimeId] - Optional runtime id, if none is provided one will be created automatically, must be unique,
 *                                        used for identifying parameters with the same id across multiple plugins
 * @param {Number} [settings.ajaxTimeout=30000] - Timeout in milliseconds for ajax requests
 * @param {Number} [settings.loggingLevel] - Initial logging level - will be overridden once plugin gets registered, hence this is rarely used
 * @param {Number} [settings.messageLoggingLevel] - Initial message logging level - will be overridden once plugin gets registered, hence this is rarely used
 */
var CommPlugin = function(___settings) {

  var that = this;

  // default model view urls
  if (typeof ___settings.modelViewUrl === 'string' && ___settings.modelViewUrl.length > 0) {
    if ( ___settings.modelViewUrl === 'eu-central-1') {
      ___settings.modelViewUrl = 'https://sdeuc1.eu-central-1.shapediver.com';
    } else if ( ___settings.modelViewUrl === 'us-east-1') {
      ___settings.modelViewUrl = 'https://model-view.shapediver.com';
    } else if ( ___settings.modelViewUrl === 'us-east-1-test') {
      ___settings.modelViewUrl = 'https://sdtest.us-east-1.shapediver.com';
    } else if ( !___settings.modelViewUrl.startsWith('http') ) {
      ___settings.modelViewUrl = 'https://' + ___settings.modelViewUrl;
    }
  }

  // create an id per instance of the CommPlugin, to be used as information for communication with the backend
  const COMMPLUGIN_INSTANCE_ID = GlobalUtils.createRandomId();

  ////////////
  ////////////
  //
  // Plugin
  //
  ////////////
  ////////////

  // we make a deep copy of the settings we were given, and inject the default settings if they are not already defined
  pluginPrototype.call(this, GlobalUtils.inject( commPluginConstants.defaultSettings, GlobalUtils.deepCopy(___settings), false ) );

  ////////////
  ////////////
  //
  // Axios - defaults
  //
  ////////////
  ////////////

  var _axios = axios.create({
    timeout: that.getSettingShallow('ajaxTimeout')
  });

  // if we were given data for an 'Authorization' header, use it
  // we expect the caller to have prepended 'Bearer ' if this is required
  if (that.getSettingShallow('authorization')) {
    // to be reviewed #SS-932: setting defaults using _axios.defaults.headers.common seems to
    // influence all instances of axios, i.e. also ones used in the glb and the texture loader
    // ideally the authorization header should be set separately for each instance
    _axios.defaults.headers.common['Authorization'] = that.getSetting('authorization');
  }

  ////////////
  ////////////
  //
  // State
  //
  ////////////
  ////////////

  // if we don't have a ticket, we have to shut down
  if (!this.hasSetting('ticket')) {
    that.error('CommPlugin.CommPlugin', 'Plugin was initialized without a ticket.');
    that.setStatus(pluginConstantsGlobal.pluginStatuses.FAILED);
    return;
  }

  /**
   * Action definitions
   * Every member needs to have at least a 'method' and a 'href' member
   * @type {Object}
   */
  var _actions = {};

  /**
   * Plugin capabilities
   * @type {module:PluginConstantsGlobal~PluginCapability[]}
   */
  var _capabilities = [
    pluginConstantsGlobal.pluginCapabilities.SHAPEDIVER_MODEL_VIEW
  ];

  /**
   * Container for remembering latest version ids for requested output ids
   * @type {Object}
   */
  var _latestOutputVersion = {};

  ////////////
  ////////////
  //
  // Private methods
  //
  ////////////
  ////////////

  /**
   * Request scene definition for a specific parameter set from the model view interface
   * and message the individual OutputVersionIds to the ViewerApp as soon as they arrive
   * @param  {Object} parameterSet Index of parameter values indexed by their ids
   * @param  {*} token    Process identification token
   * @return {Promise<module:JSONOutputVersion~JSONOutputVersion[]>} items of the result array might be undefined, in case
   *                               processing for the given output version was superseded by another customization request
   */
  var customizeScene = function(parameterSet, token) {
    //let scope = 'CommPlugin.customizeScene';

    return new customizationRequest(parameterSet)
      .then(
        function(customizationReply) {

          // check for user message
          if (customizationReply.hasOwnProperty('msg')) {
            let msg = {message: customizationReply.msg, scope: 'Customization reply', messageLevel: loggingConstants.loggingLevels.ERROR};
            let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, msg, token);
            that.message(messagingConstants.messageTopics.STATUS_MESSAGE, m);
          }
          // sanity check
          if (!customizationReply.hasOwnProperty('outputs')) {
            return Promise.reject('Invalid customization reply');
          }

          let outputIds = Object.keys(customizationReply.outputs);
          let outputPromises = [];

          // send message to world about beginning new sub-scene definition
          {
            let m1 = new MessagePrototype(messagingConstants.messageDataTypes.SUBSCENE_INIT, outputIds.length, token);
            that.message(messagingConstants.messageTopics.SCENE_SUBSCENE_INIT, m1);
            let m2 = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_STATUS,
              {state: messagingConstants.messageTopics.SCENE_SUBSCENE_INIT, progress: 0.25}, token);
            that.message(messagingConstants.messageTopics.PROCESS, m2);
          }

          // get content for all output ids
          for (let oid of outputIds) {
            // keep local reference to output id
            let outputId = oid;
            // define a Promise which will resolve to a JSONOutputVersion object
            let p = new Promise(
              function(resolve, reject) {
                let coutput = customizationReply.outputs[outputId];
                if (coutput.hasOwnProperty('content')) {
                  // we have received content, done
                  resolve(coutput);
                  return;
                }
                let versionId = coutput.version;
                // remember the latest version id requested and make sure to cancel repeated cache requests if version got superseded
                _latestOutputVersion[outputId] = versionId;
                // repeatedly send cache requests until we get content
                // we might add a timeout here, or a maximum number of cache requests
                let recursiveCacheRequest = function() {
                  return cacheRequest(outputId, versionId)
                    .then(
                      function(cacheReply) {
                        // check for the latest version id requested for this output id and cancel further processing if version got superseded in the meanwhile
                        if ( that.getSettingShallow('ignoreSuperseded') && _latestOutputVersion[outputId] !== versionId ) {
                          let m = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_ABORT, 'Subscene obsolete because of newer request', token);
                          that.message(messagingConstants.messageTopics.PROCESS, m);
                          resolve();
                          return;
                        }
                        // check for user message
                        if (cacheReply.hasOwnProperty('msg')) {
                          let msg = {message: cacheReply.msg, scope: 'Cache reply', messageLevel: loggingConstants.loggingLevels.WARN};
                          let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, msg, token);
                          that.message(messagingConstants.messageTopics.STATUS_MESSAGE, m);
                        }
                        // sanity checks
                        if (!cacheReply.hasOwnProperty('outputs') || !cacheReply.outputs.hasOwnProperty(outputId)) {
                          reject(new Error('Invalid cache reply'));
                          return;
                        }
                        let outp = cacheReply.outputs[outputId];
                        if (!outp.hasOwnProperty('version') || outp.version !== versionId) {
                          reject(new Error('Invalid cache reply'));
                          return;
                        }
                        // check if a delay has been specified and we should send another cache request
                        if (outp.hasOwnProperty('delay')) {
                          // send another cache request, using a minimum delay of 100 msec
                          setTimeout(
                            function() {
                              recursiveCacheRequest();
                            },
                            Math.max( 100, parseInt(outp.delay))
                          );
                        } else {
                          // no delay has been specified, i.e. there must be content, if not then sth is very wrong
                          if (outp.hasOwnProperty('content')) {
                            outp.id = outputId;
                            resolve(outp);
                          } else {
                            reject(new Error('No content nor delay in cache reply'));
                          }
                        }
                      }
                      // we do not catch errors of the cache request here, because catching of general errors is done by our caller
                      // , function(error) {
                      //
                      // }
                    )
                  ;
                };
                recursiveCacheRequest();
              }
            )
              .then( // process the JSONOutputVersion object
                function(output) {
                  // we might not receive a JSONOutputVersion object here, in case
                  // processing for the given output version was superseded by another customization request
                  if ( !output ) return;
                  // set output id as part of output object
                  output.id = outputId;
                  // check for user message
                  if (output.hasOwnProperty('msg')) {
                    let msg = {message: output.msg, scope: 'Output', messageLevel: loggingConstants.loggingLevels.WARN};
                    let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, msg, token);
                    that.message(messagingConstants.messageTopics.STATUS_MESSAGE, m);
                  }
                  // send message containing output - to be used by scene manager
                  let m = new MessagePrototype(messagingConstants.messageDataTypes.SUBSCENE_OUTPUTVERSION_JSON, output, token);
                  that.message(messagingConstants.messageTopics.SCENE_SUBSCENE_OUTPUTVERSION, m);
                  // return output
                  return output;
                }
                // we do not catch errors here, because catching of general errors is done by our caller
                // , function(error) {
                //
                // }
              )
            ;
            outputPromises.push(p);
          }
          return Promise.all(outputPromises);
        }
        // we do not catch errors here, because catching of general errors is done by our caller
        // , function(error) {
        //
        // }
      )
      // Alex: In the future we might uncomment the following catch block to display an error message to the user
      // should a customization request fail. We might want to do this only if the server sent us a serverMessage,
      // e.g. in case embedding of the model was forbidden while our session was active.
      // .catch(
      //   function(err) {
      //     that.error(scope, 'Customization request failed', err);
      //     // set plugin status to FAILED
      //     that.setStatus(pluginConstantsGlobal.pluginStatuses.FAILED);
      //     // send a STATUS_FAILED message which will show a "blue screen" message
      //     let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, {
      //       messageScope: err.serverMessage || 'Customization request failed'
      //     });
      //     that.message(messagingConstants.messageTopics.STATUS_FAILED, m);
      //   }
      // )
    ;
  };

  /**
   * Request export definition for a specific parameter set
   * @param  {String} id - The id fo the requested export
   * @param  {Object} kvps - Object whose own properties serve as key value pairs (parameter id, parameter value)
   * @param  {String|module:MessagingConstants~MessageToken} token - To be passed on by any operation in response to this request
   * @return {Promise<module:JSONExport~JSONExportResult>} Export data
   */
  var requestExport = function(id, parameterSet, token) {

    // define a function for processing replies to export and export-cache requests
    let processExportReply = function(exportReply) {

      // reply sanity check
      if (!exportReply.hasOwnProperty('exports') || !exportReply.exports.hasOwnProperty(id)) {
        return Promise.reject('Unexpected reply to export request');
      }

      // check if we are done
      let e = exportReply.exports[id];
      if (!e.hasOwnProperty('delay')) {
        return e;
      }

      // send notification about export status and export cache request
      let m = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_STATUS, {data: e}, token);
      that.message(messagingConstants.messageTopics.PROCESS, m);
      let m2 = new MessagePrototype(messagingConstants.messageDataTypes.EXPORT_STATUS, e, token);
      that.message(messagingConstants.messageTopics.EXPORT_STATUS, m2);

      // ask once again for export result, using a minimum delay of 1 second
      return GlobalUtils.waitForTimeout( Math.max( 1000, parseInt(e.delay)) )
        .then(
          function() {
            return exportCacheRequest(id, e.version)
              .then(
                processExportReply
              )
            ;
          }
        )
      ;
    };

    // send export request
    return exportRequest(id, parameterSet)
      .then(
        processExportReply
      )
    ;

  };


  /**
   * Initializes a model view interface session and reports parameters and settings
   * to the viewer app.
   */
  var initSession = function() {
    let scope = 'CommPlugin.initSession';

    return sessionInitRequest()
      .then(
        function(sessionInitReply) {
          that.debug(scope, 'Received session init reply', sessionInitReply);
          // check for user message
          if (sessionInitReply.hasOwnProperty('msg')) {
            let msg = {message: sessionInitReply.msg, scope: 'Session init', messageLevel: loggingConstants.loggingLevels.ERROR};
            let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, msg);
            that.message(messagingConstants.messageTopics.STATUS_MESSAGE, m);
          }
          // send messages for registration of config, parameters, exports
          let m = new MessagePrototype(messagingConstants.messageDataTypes.APP_SETTINGS, sessionInitReply.config);
          that.message(messagingConstants.messageTopics.SETTINGS_REGISTER, m);

          if (sessionInitReply.hasOwnProperty('parameters')) {
            let m = new MessagePrototype(messagingConstants.messageDataTypes.PARAMETER_DEFINITION, sessionInitReply.parameters);
            that.message(messagingConstants.messageTopics.PARAMETER_REGISTER, m);
          }
          if (sessionInitReply.hasOwnProperty('exports')) {
            let m = new MessagePrototype(messagingConstants.messageDataTypes.EXPORT_DEFINITION, sessionInitReply.exports);
            that.message(messagingConstants.messageTopics.EXPORT_REGISTER, m);
          }

          // we are now ready to receive parameter updates, update status
          if (that.getStatus() !== pluginConstantsGlobal.pluginStatuses.ACTIVE) {
            that.setStatus(pluginConstantsGlobal.pluginStatuses.ACTIVE);
          }
        }
      )
      .catch(
        function(err) {
          that.error(scope, 'Session init failed', err);
          // set plugin status to FAILED
          that.setStatus(pluginConstantsGlobal.pluginStatuses.FAILED);
          // send a STATUS_FAILED message which will show a "blue screen" message
          let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, {
            messageScope: err.serverMessage || 'Session initialisation failed'
          });
          that.message(messagingConstants.messageTopics.STATUS_FAILED, m);
        }
      )
    ;
  };


  ////////////
  ////////////
  //
  // AJAX
  //
  ////////////
  ////////////

  /**
   * The base URL which constitutes the first part of any URL used to connect
   * to the model view interface during a session, and includes the session id.
   * If a session times out and a new one is requested, this string is used to
   * replace that part with the new one including the new session id.
   *
   * @type {String}
   */
  var _baseUrl = null;

  /**
   * Custom ajax wrapper which updates session information and
   * displays user messages included in the response before passing it on
   *
   * @param  {string} url         The url to be contacted
   * @param  {string} method      One of get, post, ...
   * @param  {string} requestData        Data to be sent with the request body
   * @param  {string} contentType Content type descriptor, most commonly 'application/json'
   * @return {Promise}            A promise which resolves to the response data if the request is successful
   */
  var modelViewAjax = function(url, method, requestData, contentType) {
    var scope = 'CommPlugin.modelViewAjax';

    // basic request config
    var config = {
      method: method,
      url: url,
      headers: {},
    };

    // data to post
    if (requestData) config.data = requestData;

    // attach X-ShapeDiver-Origin header
    config.headers['X-ShapeDiver-Origin'] = GlobalUtils.embeddingOrigin();

    // attach X-ShapeDiver-CommPluginId and X-ShapeDiver-ViewerId headers
    config.headers['X-ShapeDiver-CommPluginId'] = COMMPLUGIN_INSTANCE_ID;
    config.headers['X-ShapeDiver-ViewerId'] = VIEWER_INSTANCE_ID;
    config.headers['X-ShapeDiver-BuildVersion'] = BUILD_DATA.buildVersion;
    config.headers['X-ShapeDiver-BuildDate'] = BUILD_DATA.buildDate;

    // optionally set the content type header
    if (contentType) {
      config.headers['Content-Type'] = contentType;
    }

    // if we were given data for an 'Authorization' header, use it
    // we expect the caller to have prepended 'Bearer ' if this is required
    if (that.getSettingShallow('authorization')) {
      config.headers['Authorization'] = that.getSetting('authorization');
    }

    // optionally set origin header (required for expo apps, which otherwise don't send an origin)
    //config.headers['Origin'] = 'ar.shapediver.com';

    // HTTP basic auth example
    // config.auth = {
    //   username: 'janedoe',
    //   password: 's00pers3cret'
    // };

    let success = (response) => {
      let responseObject = response.data;

      // store possible actions
      if (responseObject.hasOwnProperty('actions')) {
        responseObject.actions.forEach(function(action) {
          if (action.hasOwnProperty('method') && action.hasOwnProperty('href') && action.hasOwnProperty('name')) {
            _actions[action.name] = action;
          }
        });
        if (_actions.hasOwnProperty('default')) {
          let newBaseUrl = _actions.default.href.replace('/default','');
          if ( newBaseUrl !== _baseUrl) {
            _baseUrl = newBaseUrl;
            that.debug(scope, 'Setting base URL to', _baseUrl);
          }
        }
        delete responseObject.actions;
      }

      // remove templates
      if (responseObject.hasOwnProperty('templates')) {
        delete responseObject.templates;
      }

      return responseObject;
    }

    let counter_preFlight = 0;
    let counter_502 = 0;
    const max_retries = 3;

    let error = (error) => {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx

        // deal with non-ok http status reports
        let publicMessage = '';
        let prevBaseUrl = '';
        let errorResponseData = error.response.data;

        switch (error.response.status) {
          case 400:
            publicMessage = 'Bad request';
            break;

          case 401:
            publicMessage = 'Unauthorized';
            break;

          case 403:
            publicMessage = 'Forbidden';
            break;

          case 410:
            // The session has time out, we request a new one and repeat the original request
            prevBaseUrl = GlobalUtils.deepCopy(_baseUrl);
            return sessionInitRequest()
              .then(
                function() {
                  let updatedUrl = url.replace(prevBaseUrl, _baseUrl);
                  return modelViewAjax(updatedUrl, method, requestData, contentType);
                },
                function(err) {
                  // set plugin status to FAILED
                  that.setStatus(pluginConstantsGlobal.pluginStatuses.FAILED);
                  // send a STATUS_FAILED message which will show a "blue screen" message
                  let m = new MessagePrototype(messagingConstants.messageDataTypes.STATUS_MESSAGE, {
                    messageScope: err.serverMessage || 'Session initialisation failed'
                  });
                  that.message(messagingConstants.messageTopics.STATUS_FAILED, m);
                  return Promise.reject(err);
                }
              )
            ;

          case 413:
            publicMessage = 'Payload too large';
            break;

          case 500:
            publicMessage = 'Internal server error';
            break;

          case 502:
            counter_502++;
            if(counter_502 <= max_retries) return request(config)
            publicMessage = 'Error';
            break;

          default:
            publicMessage = 'Communication failed with HTTP status';
            break;
        }

        // publish error message
        let m = 'Request error: ' + publicMessage + ' (' + error.response.status + ')';

        // reject with error
        let err = new Error(publicMessage);
        // check if we received an error from the server
        if (errorResponseData && errorResponseData.message) {
          err.serverMessage = errorResponseData.message;
          m += ', ' + errorResponseData.message;
        }

        // publish error message
        that.error(scope, m, error);

        // reject with our own error object
        return Promise.reject(err);

      } else if (error.request) {
        // The request was made but no response was received
        let m = 'Request error (no response)';
        that.error(scope, m, error);
        counter_preFlight++;
        return counter_preFlight <= max_retries ? request(config) : Promise.reject(new Error(m));

      } else {
        // Something happened in setting up the request that triggered an Error
        let m = 'Request error (unknown)';
        that.error(scope, m, error);
        return Promise.reject(new Error(m));

      }
    };

    // let fakeAxios = () => {
    //   return new Promise((success, error) => {
    //     error({
    //       request: {
    //         data: {
    //           message: 'abc'
    //         },
    //         status: 502
    //       }});
    //   });
    // };

    let request = (config) => {
      return _axios(config)
        .then(success, error);
    }

    return request(config);
  };

  /**
   * Request to open a new session at the model view interface
   * This alone does not process any of the returned session information except
   * updating the session tokens in all action settings
   * @return {Promise} Promise resolving with the session init reply object
   */
  var sessionInitRequest = function() {
    var bUrl = that.getSettingShallow('modelViewUrl');
    if (!bUrl)
      return Promise.reject(new Error('Base url not defined.'));
    return modelViewAjax(bUrl + '/ticket/' + that.getSettingShallow('ticket'), 'POST');
  };

  /**
   * Send request for the data of a specific version id
   * @param  {String} outputId  The output id we are interested in
   * @param  {String} versionId The version id
   * @return {Promise}          A promise which resolves to an output definition JSON object which might include delays
   */
  var cacheRequest = function(outputId, versionId) {
    return new Promise(function(resolve, reject) {
      if (!_actions.hasOwnProperty('cache') || !_actions.cache.hasOwnProperty('method') || !_actions.cache.hasOwnProperty('href')) {
        let m = 'Action "cache" is missing';
        reject(new Error(m));
        return;
      }
      var cacheRequestData = {};
      cacheRequestData[outputId] = versionId;
      resolve(modelViewAjax(_actions.cache.href, _actions.cache.method, cacheRequestData, 'application/json'));
    });
  };

  /**
   * Request for the server to return export data based on a set of parameter
   * values.
   * We do not perform parameter sanity checks at this point, that must be done earlier.
   *
   * @param  {String} id              The export id.
   * @param  {Object} parameterValues Object with parameter ids as keys and respective parameter values.
   * @return {Promise}                A promise which resolves to an export definition JSON object which might include delays
   */
  var exportRequest = function(id, parameterValues) {
    return new Promise(function(resolve, reject) {
      if (!_actions.hasOwnProperty('export') || !_actions.export.hasOwnProperty('method') || !_actions.export.hasOwnProperty('href')) {
        reject(new Error('Action "export" is missing'));
      }
      let payload = {exports: {id: id}, parameters: parameterValues};
      resolve(modelViewAjax(_actions.export.href, _actions.export.method, payload, 'application/json'));
    });
  };

  /**
   * Send request for the data of a specific export version id
   * @param  {String} id  The export id we are interested in
   * @param  {String} versionId The version id
   * @return {Promise}          A promise which resolves to an export definition JSON object which might include delays
   */
  var exportCacheRequest = function(id, versionId) {
    return new Promise(function(resolve, reject) {
      if (!_actions.hasOwnProperty('export-cache') || !_actions['export-cache'].hasOwnProperty('method') || !_actions['export-cache'].hasOwnProperty('href')) {
        let m = 'Action "export-cache" is missing';
        reject(new Error(m));
        return;
      }
      var cacheRequestData = {};
      cacheRequestData[id] = versionId;
      resolve(modelViewAjax(_actions['export-cache'].href, _actions['export-cache'].method, cacheRequestData, 'application/json'));
    });
  };

  /**
   * Request for the server to process a set of parameter values
   * We do not perform parameter sanity checks at this point, that must be done earlier.
   * @param  {Object} parameterValues Object with parameter ids as keys and respective parameter values.
   * @return {Promise}                A promise which resolves to an output definition JSON object which might include delays
   */
  var customizationRequest = function(parameterValues) {
    return new Promise(function(resolve, reject) {
      if (!_actions.hasOwnProperty('customize') || !_actions.customize.hasOwnProperty('method') || !_actions.customize.hasOwnProperty('href')) {
        reject(new Error('Action "customize" is missing'));
      }
      resolve(modelViewAjax(_actions.customize.href, _actions.customize.method, parameterValues, 'application/json'));
    });
  };



  ////////////
  ////////////
  //
  // Plugin API
  //
  ////////////
  ////////////


  /**
   CommPlugin name
   @public
   @return {String} name of plugin
   */
  this.getName = function() {
    return 'ShapeDiver Communications Plugin';
  };

  /**
   CommPlugin short name, used as logging prefix
   @public
   @return {String} short name of plugin
   */
  this.getShortName = function() {
    return 'CommPlugin';
  };

  /**
   CommPlugin plugin capabilities
   @public
   @return {module:PluginConstantsGlobal~PluginCapability[]} array of plugin capabilities
   */
  this.getCapabilities = function() {
    let caps = GlobalUtils.deepCopy(_capabilities);
    if ( _actions.hasOwnProperty('log') ) {
      caps.push(pluginConstantsGlobal.pluginCapabilities.LOGGING);
    }
    if ( _actions.hasOwnProperty('configure') ) {
      caps.push(pluginConstantsGlobal.pluginCapabilities.SETTINGS);
    }
    if ( _actions.hasOwnProperty('defaultparam') ) {
      caps.push(pluginConstantsGlobal.pluginCapabilities.DEFAULTPARAM);
    }
    return caps;
  };


  /**
   * Returns an object whose properties define callbacks to be invoked for the message topics
   * defined by the property names.
   * This function will be invoked if the plugin reports the {@link module:PluginConstantsGlobal~PluginCapabilities~MESSAGECALLBACKS} capability.
   * @public
   * @return {Object} key value pairs defining callbacks for message topics
   */
  this.getMessageCallbacks = function() {
    let cbs = {};
    return cbs;
  };

  /**
   CommPlugin load function
   @public
   @param {Object} [settings] - optional settings to override default settings
   @return {Boolean} true in case of success
   */
  this.load = function(settings) {

    // update settings
    that.updateSettings(settings);

    // initialize session
    initSession();

    // load finished
    that.debug('CommPlugin.load', 'Loaded plugin "' + that.getName() + '" with capabilities ' + JSON.stringify(that.getCapabilities()) + ' and runtime id ' + that.getRuntimeId());
    that.setStatus(pluginConstantsGlobal.pluginStatuses.LOADED);
    return true;
  };

  /**
   * Attempts to send a log message to the model view interface
   * @public
   * @param {module:LoggingConstants~LoggingLevel} loggingLevel - logging level to be used
   * @param {String} scope - scope to be used for the log message, the convention is CLASSNAME.MEMBERNAME
   * @param {...Object} msg - arbitrary log message object
   * @return {Promise} Resolves to the server's response object if the message was sent successfully.
   */
  this.logToServer = function() {
    let scope = 'CommPlugin.logToServer: ';

    // divide arguments into loggingLevel, scope, and msgs
    var args = (arguments.length === 1 ? [arguments[0]] : Array.apply(null, arguments));
    var loggingLevel = args[0] & loggingConstants.loggingLevels.LVLBITS;
    var scopeMsg = args[1];
    var msgs = args.slice(2);
    if (msgs.length > 0 && typeof msgs[0] === 'string') {
      scopeMsg += ': ' + msgs[0];
      msgs = msgs.slice(1);
    }

    return new Promise(function(resolve, reject) {

      if (!_actions.hasOwnProperty('log') || !_actions.log.hasOwnProperty('method') || !_actions.log.hasOwnProperty('href')) {
        reject(new Error(scope + 'Session did not provide log action'));
      }

      // convert level
      var logMessage = {
        level: 2 - Math.max(Math.min(loggingLevel,2),0),
        message: scopeMsg
      };
      if ( msgs.length === 1 ) {
        logMessage.err = msgs[0];
      }
      else if ( msgs.length > 1 ) {
        logMessage.err = {messages: msgs};
      }

      modelViewAjax(_actions.log.href, _actions.log.method, logMessage, 'application/json').then(function(response) {
        resolve(response);
      }, function(reason) {
        var errMessage = scope + 'Log message rejected by server ' + that.jsify(reason);
        reject(new Error(errMessage));
      });
    });
  };

  /**
   * Set parameter value
   *
   * NOT SUPPORTED BY THIS PLUGIN!!!
   */
  this.setParameterValue = function() {
    that.warn('CommPlugin.setParameterValue', 'Not supported');
    return pluginConstantsGlobal.setParameterResults.ERROR;
  };

  /**
   * Set multiple parameter values
   *
   * NOT SUPPORTED BY THIS PLUGIN!!!
   */
  this.setMultipleParameterValues = function() {
    that.warn('CommPlugin.setMultipleParameterValues', 'Not supported');
    return pluginConstantsGlobal.setParameterResults.ERROR;
  };

  /**
   * Set all parameter values
   *
   * Must not be called before the plugin enters status ACTIVE {@link module:PluginConstantsGlobal~PluginStatuses}.
   * @public
   * @param {Object} kvps - Object whose own properties serve as key value pairs (parameter id or name, parameter value)
   * @param {String|module:MessagingConstants~MessageToken} token - Will be used by process messages related to this request.
   * @return {module:PluginConstantsGlobal~SetParameterResult} when/how the plugin is going to respond to the parameter update
   */
  this.setAllParameterValues = function(kvps, token) {
    if (that.getStatus() !== pluginConstantsGlobal.pluginStatuses.ACTIVE) {
      let m = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_ERROR, 'Plugin not active', token);
      that.message(messagingConstants.messageTopics.PROCESS, m);
      return pluginConstantsGlobal.setParameterResults.ERROR;
    }
    customizeScene(kvps, token)
      .catch(
        function(err) {
          // some error happened, send a process error message
          let m = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_ERROR, err, token);
          that.message(messagingConstants.messageTopics.PROCESS, m);
          // #SS-974 log the error
          // display some message to the user
          // in this case we are potentially left behind with a state of parameter values that does not correspond to the state of the scene,
          // ideally we would reset the parameter state to the previous one
        }
      )
    ;
    return pluginConstantsGlobal.setParameterResults.LOAD;
  };

  /**
   * File upload
   * Requests file upload urls, in case of success uploads blob data to those urls
   * @param {Object} requestData - object containing request data and corresponding blobs
   * @return {Promise} resolves to object containing the assigned id for each file
   */
  this.fileUpload = function(requestData) {
    let scope = 'CommPlugin.fileUpload';
    // send file upload request
    if ( !_actions.hasOwnProperty('upload') ) {
      throw new Error(scope + ': no upload action');
    }
    return modelViewAjax(_actions.upload.href, _actions.upload.method, requestData.request, 'application/json')
      .then(
        function(response) {
          // collect data for return value
          let retval = {};
          // for each parameter the response should contain an id and an upload url (href)
          let uploadPromises = [];
          for (let paramId in requestData.data) {
            // check response for parameter
            let paramResponse = response[paramId];
            if (!paramResponse || !GlobalUtils.typeCheck(paramResponse.id, 'string') || !GlobalUtils.typeCheck(paramResponse.href, 'string') ) {
              throw new Error(scope + ': invalid response');
            }
            // id needs to be returned
            retval[paramId] = paramResponse.id;
            //
            // config for PUTting the blob to the url we got
            let config = {
              method: 'PUT',
              url: paramResponse.href,
              data: requestData.data[paramId],
              headers: {},
              // make sure we don't send any Authorization header
              transformRequest: [(data, headers) => {
                delete headers.common.Authorization;
                delete headers.Authorization;
                return data;
              }]
            };
            // set the content type for the PUT request
            config.headers['Content-Type'] = requestData.request[paramId].format;
            // PUT the blob to the url we got
            uploadPromises.push(_axios(config));
          }
          // wait for all uploads
          return Promise.all(uploadPromises)
            .then(
              function() {
                return retval;
              }
            )
          ;
        }
        // no need to catch here
      )
    ;
  };

  /**
   * Request export for given export id and parameter values
   *
   * Must not be called before the plugin enters status ACTIVE {@link module:PluginConstantsGlobal~PluginStatuses}.
   * @public
   * @param {String} id - id of export to request
   * @param {Object} kvps - Object whose own properties serve as key value pairs (parameter id, parameter value)
   * @param {String|module:MessagingConstants~MessageToken} token - Will be used by process messages related to this request.
   * @return {module:PluginConstantsGlobal~RequestExportResults} when/how the plugin is going to respond to the export request
   */
  this.requestExport = function(id, kvps, token) {
    let scope = 'CommPlugin.requestExport';

    // check status of plugin
    if (that.getStatus() !== pluginConstantsGlobal.pluginStatuses.ACTIVE) {
      let m = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_ERROR, 'Plugin not active', token);
      that.message(messagingConstants.messageTopics.PROCESS, m);
      return pluginConstantsGlobal.setParameterResults.ERROR;
    }

    // start the asynchronous export request
    requestExport(id, kvps, token)
      .then(
        function(exportData) {
          let m = new MessagePrototype(messagingConstants.messageDataTypes.EXPORT_RESULT, exportData, token);
          that.message(messagingConstants.messageTopics.EXPORT_AVAILABLE, m);
          // message to notify about process success
          let m2 = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_SUCCESS, 'Export available', token);
          m2.addPart(messagingConstants.messageDataTypes.EXPORT_RESULT, exportData);
          that.message(messagingConstants.messageTopics.PROCESS, m2);
        },
        function(err) {
          // some error happened, send a process error message
          let m = new MessagePrototype(messagingConstants.messageDataTypes.PROCESS_ERROR, err, token);
          that.message(messagingConstants.messageTopics.PROCESS, m);
          // #SS-974 log the error
        }
      )
    ;

    that.debug(scope, 'Called with export ' + id + ', kvps ' + that.jsify(kvps) + ', token ' + that.jsify(token));
    return that.pluginConstants.requestExportResults.LOAD;
  };

  /**
   * Save model settings object to backend
   *
   * Must not be called before the plugin enters status ACTIVE {@link module:PluginConstantsGlobal~PluginStatuses}.
   * Fails in case session is not allowed to save settings.
   * @public
   * @param {Object} settings - settings object
   * @return {Promise<Boolean>} resolves to true if saving the settings has succeeded, rejects otherwise
   */
  this.saveSettings = function(settings) {
    if ( !_actions.hasOwnProperty('configure') ) return Promise.resolve(false);
    return modelViewAjax(_actions.configure.href, _actions.configure.method, settings, 'application/json')
      .then(
        function() {
          return true;
        }
      )
    ;
  };

  /**
   * Save default parameter values to backend
   *
   * Must not be called before the plugin enters status ACTIVE {@link module:PluginConstantsGlobal~PluginStatuses}.
   * Fails in case session is not allowed to save default parameter values.
   * @public
   * @param {Object} kvps - Object whose own properties serve as key value pairs (parameter id or name, parameter value)
   * @return {Promise<Boolean>} resolves to true if saving the default parameters has succeeded, rejects otherwise
   */
  this.saveDefaultParameterValues = function(kvps) {
    if ( !_actions.hasOwnProperty('defaultparam') ) return Promise.resolve(false);
    return modelViewAjax(_actions.defaultparam.href, _actions.defaultparam.method, kvps, 'application/json')
      .then(
        function() {
          return true;
        }
      )
    ;
  };

  ////////////
  ////////////
  //
  // Settings
  //
  ////////////
  ////////////

  /**
   Create hook for updateSetting of modelViewUrl which prohibits any update of this setting
   See SettingsMixin.updateSetting for details about this
   {String} modelViewUrl - new model view url to be used from now on
   */
  this.registerHook('modelViewUrl',() => false);

  /**
   Create hook for updateSetting of the ticket which prohibits any update of this setting
   See SettingsMixin.updateSetting for details about this
   {String} ticket - new ticket
   */
  this.registerHook('ticket',() => false);

  ////////////
  ////////////
  //
  // Finish initialization
  //
  ////////////
  ////////////


  // we are now ready to load
  this.setStatus(pluginConstantsGlobal.pluginStatuses.READY);


};



// export the constructor
module.exports = CommPlugin;
